import { useMemo, useState } from 'react';
import { navTree, navLabel, type Badge, type NavItem } from '../../layouts/navData';
import { NavIcon } from '../../components/NavIcon';

// Home — v5 design (alpaca_intelligence_v5.html) but the module catalog is
// generated from navData so EVERY group/module we actually have is listed.

type Pill = 'sp-view' | 'sp-sub' | 'sp-ai' | 'sp-new';
interface Section { icon: string; ic: string; name: string; items: NavItem[]; }

/** Short descriptions for the well-known modules (others fall back to label). */
const DESC: Record<string, string> = {
  home: 'ALPACA Intelligence landing page · platform overview',
  execdash: 'KPIs, job trends, and status distribution across all projects',
  avainsight: 'Anomaly detection, trend alerts, natural-language lab analysis',
  trendanalysis: 'Parameter trend charts across BTLIMSDC + ALPACA — interactive, PDF/Excel export',
  histres: 'Browse 134K+ historical results across BTLIMSDC + ALPACA — 2Y default',
};

const PILL: Record<Badge, { cls: Pill; txt: string }> = {
  intel: { cls: 'sp-sub', txt: 'Intel' },
  ai: { cls: 'sp-ai', txt: 'AI' },
  new: { cls: 'sp-new', txt: 'New' },
  view: { cls: 'sp-view', txt: 'View' },
};

/** Round-robin icon-chip colour classes (matches v5 .ic-* palette). */
const IC = ['ic-p', 'ic-o', 'ic-b', 'ic-t', 'ic-a', 'ic-c', 'ic-g'];

/** Build the section list from navData, filtered by the current user's
 *  permissions.  Sections containing no permitted items are dropped entirely
 *  — matches the sidebar's hide-empty-group behaviour. */
function buildSections(canSee: (id: string, label: string) => boolean): Section[] {
  const out: Section[] = [];
  const intel: NavItem[] = [];
  for (const e of navTree) {
    if (e.kind === 'item' && canSee(e.item.id, e.item.label)) intel.push(e.item);
    else if (e.kind === 'group') {
      const items = e.group.items.filter(it => canSee(it.id, it.label));
      if (items.length === 0) continue;
      out.push({ icon: e.group.icon, ic: IC[out.length % IC.length]!, name: e.group.label, items });
    }
  }
  if (intel.length) {
    out.unshift({ icon: 'ti-brain', ic: 'ic-p', name: 'Intelligence', items: intel });
  }
  // re-key ic colours after unshift so they stay varied
  return out.map((s, i) => ({ ...s, ic: IC[i % IC.length]! }));
}

export function HomePage({ onOpen, canSee }: {
  onOpen: (id: string) => void;
  /** Permission predicate from the AppShell.  When omitted (e.g. used outside
   *  the shell) all items are shown. */
  canSee?: (id: string, label: string) => boolean;
}) {
  const sections = useMemo(
    () => buildSections(canSee ?? (() => true)),
    [canSee],
  );
  const total = useMemo(
    () => sections.reduce((n, s) => n + s.items.length, 0),
    [sections],
  );
  const [open, setOpen] = useState<Record<string, boolean>>({});
  const toggle = (n: string) => setOpen((o) => ({ ...o, [n]: !o[n] }));

  return (
    <>
      <div className="hero-wrap">
        <div className="hero">
          <div>
            <div className="hero-ey">ALPACA LIMS G6</div>
            <div className="hero-t">Your lab data,<br />intelligently visualized</div>
            <div className="hero-s">{total} modules · Full LIMS read-only mirror · Analytics, QC &amp; AI insights</div>
            <button className="ca-btn ca-btn-solid" onClick={() => onOpen('execdash')}>
              <i className="ti ti-layout-dashboard" /> Go to Executive Dashboard
            </button>
          </div>
          <div className="hstats">
            <div><div className="hsv">134K</div><div className="hsl">Parameters</div></div>
            <div className="hdiv" />
            <div><div className="hsv g">97.4%</div><div className="hsl">QC pass rate</div></div>
            <div className="hdiv" />
            <div><div className="hsv o">12</div><div className="hsl">Exceedances</div></div>
          </div>
        </div>
      </div>

      <div className="modules-scroll">
        <div className="legend">
          <div className="legend-item"><span className="leg-b" style={{ background: 'color-mix(in srgb, var(--brand-accent, var(--ai-orange)) 10%, #fff)', color: 'var(--brand-accent-hover, var(--ai-orange-dark))' }}>Intel</span> Intelligence module</div>
          <div className="legend-item"><span className="leg-b" style={{ background: 'var(--ai-pill-ok-bg)', color: 'var(--ai-pill-ok-fg)' }}>New</span> New in Intelligence</div>
          {/* token-exempt: AVA/AI badge violet has no palette token (matches shell.css .b-ai) */}
          <div className="legend-item"><span className="leg-b" style={{ background: '#EEEDFE', color: '#534AB7' }}>AI</span> AVA-powered</div>
          <div className="legend-item"><span className="leg-b" style={{ background: 'var(--ai-line-soft)', color: 'var(--ai-muted)' }}>View</span> Read-only LIMS mirror</div>
        </div>

        {sections.map((s) => {
          const isOpen = !!open[s.name];
          return (
            <div className="mod-section" key={s.name}>
              <div className={`mod-section-hdr${isOpen ? ' open' : ''}`} onClick={() => toggle(s.name)}>
                <div className="msh-left">
                  <div className={`msh-icon ${s.ic}`}><NavIcon icon={s.icon} /></div>
                  <div>
                    <div className="msh-name">{s.name}</div>
                    <div className="msh-sub">{s.items.slice(0, 6).map((i) => navLabel(i.id, i.label)).join(' · ')}{s.items.length > 6 ? ' · …' : ''}</div>
                  </div>
                </div>
                <div className="msh-right">
                  <span className="msh-cnt">{s.items.length} module{s.items.length === 1 ? '' : 's'}</span>
                  <i className={`ti ti-chevron-down msh-chevron${isOpen ? ' open' : ''}`} />
                </div>
              </div>
              <div className={`mod-body${isOpen ? ' open' : ''}`}>
                <table className="bo-table">
                  <thead><tr><th>Business Object</th><th>Description</th><th>Type</th></tr></thead>
                  <tbody>
                    {s.items.map((r) => {
                      const p = PILL[r.badge ?? 'view'];
                      return (
                        <tr key={r.id}>
                          <td>
                            <button className="bo-link" onClick={() => onOpen(r.id)}>
                              <NavIcon icon={r.icon} color={r.iconColor} />
                              {navLabel(r.id, r.label)}
                            </button>
                          </td>
                          <td>{DESC[r.id] ?? navLabel(r.id, r.label)}</td>
                          <td><span className={`status-pill ${p.cls}`}>{p.txt}</span></td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          );
        })}

        <div className="home-foot">ALPACA LIMS G6 v26.11.3 &nbsp;·&nbsp; © 2026 BTSoft</div>
      </div>
    </>
  );
}
